import React, { useState } from "react";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../utils/appUtils";
import {
  buildCarrierIdentityFields,
  changedCarrierIdentity,
  normalizeCarrierIdentifiers,
  normalizeCarrierPhone,
  validateCarrierUnicity,
} from "../../../utils/carrierIdentity";
import { DASHBOARD_PATH } from "../../carrierPaths";

/**
 * Save the carrier's own company profile.
 *
 * The manager's ValidateSaveButton with two differences: it returns the carrier
 * to wherever Back would have taken them — the dashboard, unless a linking screen
 * supplied a trail — rather than to a Carriers list the carrier has no access to,
 * and it never sends `email`. The company's address is what makes one of its users the
 * primary account (isCarrierAdmin reads it off the record), so a dispatcher who
 * could type their own address into it could make themselves the account nobody
 * is allowed to delete. It is left out of the input entirely rather than only
 * disabled in the form.
 *
 * Two records can be under this one button. Normally it is the CarrierCompany,
 * which stands above the yard entries and is mirrored back down to them by
 * handleCarrierChange. For an entry the stream has not placed yet there is no
 * company to write, so the entry itself is saved — which is exactly what the
 * stream then places, so the case resolves itself on the first save. The two take
 * different columns, which is what `isCompany` picks between below — passed in
 * rather than read off `resource`, so the choice is made where the subject is
 * decided instead of depending on what react-admin clones into a toolbar.
 *
 * The duplicate check is the same one the yard's forms run, against the same
 * yard book, and it stays per-yard in both cases: the carrier can reach a number
 * or a name another carrier at this transload already uses, and the yard would
 * have been stopped from doing it. Platform-wide, a number belongs to whoever
 * claimed it first — that is settled server-side in the claim table, not here.
 */
export const CompanySaveButton = ({
  record,
  carrierId,
  isCompany,
  // The same trail <BackButton> is given, so a save and a Back leave the page the
  // same way. getBackPath always yields at least the dashboard, but the default
  // is here too: the button is rendered by a toolbar react-admin clones, and a
  // save that succeeded must not strand the carrier on a form it already wrote.
  backPath = [DASHBOARD_PATH],
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isSaving, setIsSaving] = useState(false);

  const handleValidateSave = async () => {
    const formState = form.getState();

    if (formState.invalid) {
      props.handleSubmitWithRedirect();
      return;
    }

    // Only what this save moves, so a phone number the record already shares
    // with another carrier cannot refuse a change to the ID numbers.
    const changed = changedCarrierIdentity(formState.values, record);
    if (Object.keys(changed).length === 0) {
      props.handleSubmitWithRedirect();
      return;
    }

    setIsSaving(true);

    const duplicate = await validateCarrierUnicity(
      // The numbers come along whether or not they moved — they are what vetoes
      // a name collision against a carrier holding a different number under the
      // same authority.
      { ...changed, identifiers: formState.values?.identifiers },
      // The yard entry, never the company: the book being checked is one yard's
      // Carrier records, and the row to leave out of it is this carrier's own.
      { excludeId: carrierId ?? record?.id ?? formState.values?.id },
    );

    if (duplicate) {
      notify(duplicate.message, "warning");
      setIsSaving(false);
      return;
    }

    props.handleSubmitWithRedirect();
  };

  // What the company record takes. `email` and `normalizedEmail` are not here at
  // all — not disabled, not recomputed, absent — so no save from this form can
  // move the privilege anchor. `active` and `reloadYardId` belong to a yard entry
  // and have no meaning on the company.
  const transformCompany = (data) => ({
    id: data.id,
    businessName: data.businessName,
    phone: data.phone,
    normalizedPhone: normalizeCarrierPhone(data.phone),
    identifiers: normalizeCarrierIdentifiers(data.identifiers),
  });

  const transformCarrier = (data) => ({
    id: data.id,
    active: data.active,
    businessName: data.businessName,
    phone: data.phone,
    reloadYardId: data.reloadYardId,
    // Derived from the raw values on this same save, so the next duplicate check
    // reads normalised values that still match the record they belong to. `email`
    // is not editable here, so normalizedEmail is rewritten from the value the
    // record already holds rather than from anything the form could have changed.
    ...buildCarrierIdentityFields(data),
    identifiers: normalizeCarrierIdentifiers(data.identifiers),
  });

  // Off the profile rather than back onto it: it is filled in at onboarding and
  // rarely corrected, so staying put left the carrier looking at a form with
  // nothing more to do on it. The trail is consumed the way <BackButton> consumes
  // it — last entry is where we go, the rest travels on as the next page's trail.
  const onSuccess = () => {
    setIsSaving(false);
    notify("Company profile updated.");
    const trail = [...backPath];
    const destination = trail.pop() || DASHBOARD_PATH;
    redirect(destination, undefined, undefined, undefined, {
      backPath: trail,
    });
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("carrierApp:company:CompanySaveButton:onFailure", error, notify);
  };

  return (
    <SaveButton
      {...props}
      submitOnEnter={false}
      undoable={false}
      disabled={props.pristine}
      saving={isSaving}
      handleSubmitWithRedirect={handleValidateSave}
      transform={isCompany ? transformCompany : transformCarrier}
      onSuccess={onSuccess}
      onFailure={onFailure}
    />
  );
};
